import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../../redux/action.js";
import axios from "../../../../shared/axios/axiosProxy.js";
import i18n from "../../../../utils/i18n.js";
import contactClasses from "./couponCodeContactPage.module.scss";

/** Same validation rule as searchFlow `aspProviders.jsx`. */
const isValidCouponCode = (code) => {
  if (!code || typeof code !== "string") return false;
  const trimmed = code.trim();
  if (!trimmed.length) return false;
  const invalidValues = ["not set", "undefined", "null", "none", "false", "true"];
  if (invalidValues.includes(trimmed.toLowerCase())) return false;
  return /^[A-Za-z0-9_-]+$/.test(trimmed);
};

/** Canonical form for Redux, ref equality, and API — must match `onBlur` persistence. */
const normalizeCouponCode = (raw) => String(raw ?? "").trim().toUpperCase();

/**
 * Coupon code input that mirrors the searchFlow `aspProviders` behavior:
 *   - Auto-applies a code coming from `company_store.discountCode` (UTM referral).
 *   - Posts to `/api/coupons/get` and on success stores the response under
 *     `company_store.discount`, which `ProviderGradeSelection` reads to apply
 *     the price reduction.
 *   - Shows snackbars for valid / invalid coupons.
 */
const CouponCode = ({ variant = "default" }) => {
  const dispatch = useDispatch();
  const companyStore = useSelector((state) => state.company_store);
  const appliedDiscount = companyStore.discount;

  const [couponCode, setCouponCode] = useState("");
  const [snackValidOpen, setSnackValidOpen] = useState(false);
  const [snackInvalidOpen, setSnackInvalidOpen] = useState(false);

  /**
   * Tracks which `discountCode` value has already been auto-applied so the
   * effect below does NOT re-trigger `couponCheck` for the same code.
   *
   * Using a ref (instead of state) is intentional: updating the ref doesn't
   * cause a re-render, which avoids the infinite loop that the previous
   * two-effect pattern (`setDiscountCodeApplied(true)` + reset effect) could
   * provoke when state updates from the two effects raced against each other.
   */
  const appliedDiscountCodeRef = useRef(null);

  const couponCheck = useCallback(
    (data) => {
      axios
        .post(`/api/coupons/get`, data, {
          headers: { "Content-Type": "application/json" },
        })
        .then((response) => {
          setSnackValidOpen(true);
          dispatch(
            ACTIONS.addCompanyInfo({
              id: "discount",
              data: response.data,
              stateObject: "company_store",
            }),
          );
        })
        .catch(() => {
          setSnackInvalidOpen(true);
          dispatch(
            ACTIONS.addCompanyInfo({
              id: "discount",
              data: null,
              stateObject: "company_store",
            }),
          );
        });
    },
    [dispatch],
  );

  useEffect(() => {
    const storedCode = companyStore.discountCode;

    if (!storedCode?.trim()) {
      appliedDiscountCodeRef.current = null;
      return;
    }

    const normalized = normalizeCouponCode(storedCode);

    if (!isValidCouponCode(normalized)) {
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "discountCode",
          data: "",
          stateObject: "company_store",
        }),
      );
      setCouponCode("");
      appliedDiscountCodeRef.current = null;
      return;
    }

    if (normalized !== storedCode) {
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "discountCode",
          data: normalized,
          stateObject: "company_store",
        }),
      );
      return;
    }

    if (appliedDiscountCodeRef.current === normalized) {
      return;
    }

    setCouponCode(normalized);
    couponCheck({ couponCode: normalized, flow: "search" });
    appliedDiscountCodeRef.current = normalized;
  }, [companyStore.discountCode, couponCheck, dispatch]);

  const handleCloseSnack = (_event, reason) => {
    if (reason === "clickaway") return;
    setSnackValidOpen(false);
    setSnackInvalidOpen(false);
  };

  const handleApplyClick = () => {
    const normalized = normalizeCouponCode(couponCode);
    if (!isValidCouponCode(normalized)) {
      setSnackInvalidOpen(true);
      return;
    }
    appliedDiscountCodeRef.current = normalized;
    if (normalized !== companyStore.discountCode) {
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "discountCode",
          data: normalized,
          stateObject: "company_store",
        }),
      );
    }
    couponCheck({ couponCode: normalized, flow: "search" });
    setCouponCode("");
  };

  const handleRemoveDiscount = () => {
    dispatch(
      ACTIONS.addCompanyInfo({
        id: "discount",
        data: null,
        stateObject: "company_store",
      }),
    );
    dispatch(
      ACTIONS.addCompanyInfo({
        id: "discountCode",
        data: "",
        stateObject: "company_store",
      }),
    );
    appliedDiscountCodeRef.current = null;
    setCouponCode("");
  };

  const isApplied = Boolean(appliedDiscount);
  const appliedLabel =
    appliedDiscount?.couponCode ||
    appliedDiscount?.code ||
    companyStore.discountCode ||
    "";
  const canRedeem = Boolean(couponCode && isValidCouponCode(couponCode));

  const snackbars = (
    <>
      <Snackbar
        open={snackValidOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnack}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {i18n.t("searchflow.couponvalidated")}
        </Alert>
      </Snackbar>
      <Snackbar
        open={snackInvalidOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnack}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {i18n.t("searchflow.couponinvalid")}
        </Alert>
      </Snackbar>
    </>
  );

  if (variant === "contactPage") {
    return (
      <div className={contactClasses.root}>
        <p className={contactClasses.hint}>
          {i18n.t("searchflow.discounttext")}
        </p>

        {isApplied ? (
          <div className={contactClasses.appliedChip}>
            <span>{appliedLabel}</span>
            <button
              type="button"
              className={contactClasses.removeButton}
              onClick={handleRemoveDiscount}
              aria-label={i18n.t("searchflow.removecouponaria")}
            >
              ×
            </button>
          </div>
        ) : (
          <div className={contactClasses.inputRow}>
            <div className={contactClasses.fieldWrap}>
              <label className={contactClasses.fieldLabel} htmlFor="discountCode">
                {i18n.t("searchflow.couponcodelabel")}
              </label>
              <input
                id="discountCode"
                name="discountCode"
                className={contactClasses.input}
                value={couponCode}
                placeholder={i18n.t("searchflow.couponplaceholder")}
                onChange={(event) =>
                  setCouponCode(event.target.value.toUpperCase())
                }
                onBlur={(event) => {
                  const value = normalizeCouponCode(event.target.value);
                  if (isValidCouponCode(value)) {
                    dispatch(
                      ACTIONS.addCompanyInfo({
                        id: "discountCode",
                        data: value,
                        stateObject: "company_store",
                      }),
                    );
                  } else if (value === "") {
                    dispatch(
                      ACTIONS.addCompanyInfo({
                        id: "discountCode",
                        data: "",
                        stateObject: "company_store",
                      }),
                    );
                  }
                }}
                aria-label={i18n.t("searchflow.couponinputaria")}
              />
            </div>
            <button
              type="button"
              className={`${contactClasses.redeemButton} ${
                canRedeem ? contactClasses.redeemButtonActive : ""
              }`}
              disabled={!canRedeem}
              onClick={handleApplyClick}
            >
              {i18n.t("searchflow.checkcoupon")}
            </button>
          </div>
        )}

        {snackbars}
      </div>
    );
  }

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <LocalOfferOutlinedIcon fontSize="small" color="primary" />
        <Typography sx={{ fontWeight: 700 }}>
          {i18n.t("searchflow.discountlabel")}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {i18n.t("searchflow.discounttext")}
      </Typography>

      {isApplied ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Alert
            severity="success"
            variant="outlined"
            sx={{ flex: 1, minWidth: 0, py: 0.5 }}
          >
            {i18n.t("searchflow.couponvalidated")}
            {appliedLabel ? `: ${appliedLabel}` : ""}
          </Alert>
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={handleRemoveDiscount}
            sx={{ textTransform: "none" }}
          >
            {i18n.t("searchflow.removecoupon")}
          </Button>
        </Stack>
      ) : (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
          <TextField
            id="discountCode"
            name="discountCode"
            value={couponCode}
            size="small"
            placeholder="TEST"
            onChange={(event) =>
              setCouponCode(event.target.value.toUpperCase())
            }
            onBlur={(event) => {
              const value = normalizeCouponCode(event.target.value);
              if (isValidCouponCode(value)) {
                dispatch(
                  ACTIONS.addCompanyInfo({
                    id: "discountCode",
                    data: value,
                    stateObject: "company_store",
                  }),
                );
              } else if (value === "") {
                dispatch(
                  ACTIONS.addCompanyInfo({
                    id: "discountCode",
                    data: "",
                    stateObject: "company_store",
                  }),
                );
              }
            }}
            sx={{ flex: 1 }}
            inputProps={{ "aria-label": "discount code" }}
          />
          <Button
            variant="contained"
            disabled={!couponCode || !isValidCouponCode(couponCode)}
            onClick={handleApplyClick}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            {i18n.t("searchflow.checkcoupon")}
          </Button>
        </Stack>
      )}

      {snackbars}
    </Box>
  );
};

export default CouponCode;
